import styles from '@ui/components/IconCard/IconCard.module.scss';
import Button from '@ui/components/Button/Button';
import Card from '@ui/components/Card/Card';
import CardAction from '@ui/components/Card/components/CardAction/CardAction';
import Content from '@ui/components/Content/Content';
import Heading from '@ui/components/Heading/Heading';
import FontAwesomeProIcon from '@ui/components/FontAwesomeProIcon/FontAwesomeProIcon';
import Image from '@ui/components/Image/Image';
import CardBody from '@ui/components/Card/components/CardBody/CardBody';
import { VisuallyHidden } from '@ariakit/react';
import classNames from 'classnames';
import React from 'react';
import PropTypes from 'prop-types';
import { getHeadingTag } from '../../utils';

const IconCard = ({
  as = 'div',
  button,
  className,
  contentSize = 'regular',
  date,
  description,
  glyph,
  hasAction = false,
  headingSize = 'h5',
  icon,
  headingLevel = 3,
  limitHeading,
  limitText,
  onClick,
  size,
  theme = 'default',
  title,
  type,
  variant = 'default',
  ...props
}) => (
  <Card
    as={as}
    className={classNames(styles.card, className)}
    hasAction={hasAction}
    theme={theme}
    {...props}
  >
    <CardBody
      className={classNames(styles.container, {
        [styles[theme]]: theme,
        [styles[size]]: size,
        [styles.descriptionMargin]: hasAction && description,
        [styles.headingMargin]: hasAction && !description,
      })}
    >
      {!!glyph && (
        <FontAwesomeProIcon
          className={classNames(styles.icon, {
            [styles.iconTop]: variant === 'iconTop',
          })}
          fixedWidth
          icon={glyph}
          role="presentation"
        />
      )}
      {!!icon && (
        <Image
          alt={title}
          className={classNames(styles.icon, {
            [styles.iconTop]: variant === 'iconTop',
          })}
          src={icon}
        />
      )}
      <div className={styles.content}>
        {!!title && (
          <Heading
            aria-hidden={!!limitHeading}
            as={getHeadingTag(headingLevel)}
            className={styles.heading}
            noSpacing={!description}
            size={headingSize}
            theme={['contrast', 'highlight'].includes(theme) ? 'neutral' : null}
          >
            {limitHeading && title.length > limitHeading
              ? `${title.substring(0, limitHeading)}...`
              : title}
          </Heading>
        )}
        {!!limitHeading && (
          <VisuallyHidden>
            <Heading size={headingSize}>{title}</Heading>
          </VisuallyHidden>
        )}
        {!!description && (
          <Content
            aria-hidden={!!limitText}
            className={classNames(styles.cardContent, {
              [styles[type]]: type,
            })}
            noSpacing
            notResponsive
            size={contentSize}
            withParser
          >
            {limitText && description.length > limitText
              ? `${description.substring(0, limitText)}...`
              : description}
          </Content>
        )}
        {!!limitText && (
          <VisuallyHidden>
            <Content notResponsive>{description}</Content>
          </VisuallyHidden>
        )}
        {!!button && (
          <Button
            aria-hidden
            className={styles.button}
            onClick={onClick}
            tabIndex={-1}
          >
            {button.title}
          </Button>
        )}
      </div>
      {date && <div className={styles.date}>{date}</div>}
    </CardBody>
    {!button && hasAction && (
      <CardAction>
        <Button aria-hidden onClick={onClick} shape="bubble" tabIndex={-1}>
          <FontAwesomeProIcon icon="chevron-right" role="presentation" />
        </Button>
      </CardAction>
    )}
  </Card>
);

IconCard.propTypes = {
  as: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
    // ref
    PropTypes.oneOfType([
      PropTypes.func,
      PropTypes.shape({ current: PropTypes.element }),
    ]),
  ]),
  button: PropTypes.shape({
    url: PropTypes.string,
    title: PropTypes.string,
    target: PropTypes.string,
  }),
  className: PropTypes.string,
  contentSize: PropTypes.oneOf(['xs', 'sm', 'regular', 'md', 'lg']),
  date: PropTypes.string,
  description: PropTypes.string,
  glyph: PropTypes.string,
  hasAction: PropTypes.bool,
  headingLevel: PropTypes.number,
  headingSize: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'h7']),
  icon: PropTypes.string,
  limitHeading: PropTypes.number,
  limitText: PropTypes.number,
  onClick: PropTypes.func,
  size: PropTypes.oneOf(['large']),
  theme: PropTypes.oneOf(['default', 'highlight', 'contrast']),
  title: PropTypes.string,
  titleTag: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType]),
  type: PropTypes.string,
  variant: PropTypes.oneOf(['default', 'iconTop']),
};

export default IconCard;
